/* eslint-disable react/no-unescaped-entities */

import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Play, Pause, Volume2, VolumeX, Maximize2 } from "lucide-react";
import { HERO_IMAGE, HERO_VIDEO_URL } from "@/lib/content";

export default function Hero({ onCtaClick }) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);
  const [showUnmuteHint, setShowUnmuteHint] = useState(false);

  // Start muted autoplay (reliable across all browsers), then show a prominent
  // "Activer le son" button. As soon as the user interacts, we unmute.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.play().then(() => {
      setPlaying(true);
      setMuted(true);
      setShowUnmuteHint(true);
    }).catch(() => setPlaying(false));
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };

  const toggleMute = (e) => {
    if (e) e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted && v.paused) v.play();
    setMuted(v.muted);
    setShowUnmuteHint(false);
  };

  const goFullscreen = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    if (v.requestFullscreen) v.requestFullscreen();
    else if (v.webkitEnterFullscreen) v.webkitEnterFullscreen();
  };

  return (
    <section className="relative min-h-screen flex items-center pt-24 pb-12 lg:pt-20 lg:pb-0 overflow-hidden" data-testid="hero-section">
      {/* Background image with overlays */}
      <div className="absolute inset-0 z-0">
        <img
          src={HERO_IMAGE}
          alt=""
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/70" />
        <div className="absolute inset-0 bg-grid opacity-50" />
      </div>

      {/* Diagonal yellow stripe accent */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-stripes pointer-events-none z-0" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-12 py-12 lg:py-24 w-full">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-8 order-2 lg:order-1">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-3 mb-8"
            >
              <span className="h-px w-12 bg-[#FFD60A]" />
              <span className="font-mono text-xs tracking-[0.4em] text-[#FFD60A] uppercase">
                Formation N°1 Reprogrammation
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] text-white uppercase"
              data-testid="hero-title"
            >
              Devenez <span className="text-[#FFD60A]">professionnel</span> de la reprogrammation moteur
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-6 md:mt-8 text-base sm:text-lg md:text-xl text-zinc-300 max-w-2xl leading-relaxed"
              data-testid="hero-subtitle"
            >
              Apprenez à réaliser des conversions <span className="text-white font-semibold">E85</span>, des préparations <span className="text-white font-semibold">Stage 1 et Stage 2</span>, et à développer une activité rentable grâce à l'expérience terrain du réseau Horsepower.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <button
                onClick={() => onCtaClick("call_booking", { campaign: "cta-hero" })}
                className="group flex items-center justify-center gap-2 sm:gap-3 px-5 sm:px-8 py-4 bg-[#FFD60A] text-black font-bold uppercase text-sm tracking-wider hover:bg-yellow-300 transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(255,214,10,0.35)]"
                data-testid="hero-cta-primary"
              >
                <span>Réserver un appel découverte</span>
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform shrink-0" />
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-12 grid grid-cols-3 gap-6 max-w-xl"
            >
              {[
                { v: "12", l: "Modules" },
                { v: "100%", l: "Cas réels" },
                { v: "À vie", l: "Accès" },
              ].map((s, i) => (
                <div key={i} className="border-l border-zinc-800 pl-4">
                  <div className="font-display text-3xl text-[#FFD60A]">{s.v}</div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500 mt-1">{s.l}</div>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex lg:col-span-4 justify-center lg:justify-end order-1 lg:order-2"
          >
            <div className="relative w-full max-w-md lg:max-w-sm">
              <div className="absolute -inset-2 bg-[#FFD60A]/20 blur-2xl" />
              <div className="relative border border-zinc-800 bg-black/70 backdrop-blur p-1">
                <div
                  className="aspect-video lg:aspect-[3/4] bg-zinc-900 relative overflow-hidden group cursor-pointer"
                  onClick={togglePlay}
                  data-testid="hero-video-container"
                >
                  <video
                    ref={videoRef}
                    src={HERO_VIDEO_URL}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    className="w-full h-full object-cover"
                    data-testid="hero-video"
                  />
                  {/* Prominent unmute prompt - shown when video is muted */}
                  {showUnmuteHint && muted && (
                    <button
                      onClick={toggleMute}
                      className="absolute inset-x-0 bottom-3 mx-auto w-fit flex items-center gap-2 px-4 py-2.5 bg-[#FFD60A] text-black font-bold text-xs uppercase tracking-widest hover:bg-yellow-300 transition-all shadow-[0_4px_20px_rgba(255,214,10,0.5)] animate-pulse-slow z-10"
                      data-testid="hero-video-unmute-hint"
                    >
                      <Volume2 size={14} />
                      Activer le son
                    </button>
                  )}
                  {/* Centered play indicator (visible when paused) */}
                  {!playing && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 pointer-events-none">
                      <div className="w-16 h-16 rounded-full bg-[#FFD60A] flex items-center justify-center">
                        <Play size={24} className="text-black ml-1" fill="currentColor" />
                      </div>
                    </div>
                  )}
                  {/* Hover gradient + controls */}
                  <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                  <div className="absolute bottom-3 right-3 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={toggleMute}
                      className="w-9 h-9 bg-black/70 backdrop-blur border border-white/20 hover:border-[#FFD60A] flex items-center justify-center text-white transition-colors"
                      aria-label={muted ? "Activer le son" : "Couper le son"}
                      data-testid="hero-video-mute"
                    >
                      {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                    </button>
                    <button
                      onClick={goFullscreen}
                      className="w-9 h-9 bg-black/70 backdrop-blur border border-white/20 hover:border-[#FFD60A] flex items-center justify-center text-white transition-colors"
                      aria-label="Plein écran"
                      data-testid="hero-video-fullscreen"
                    >
                      <Maximize2 size={14} />
                    </button>
                  </div>
                  {/* Play/pause indicator top-left */}
                  <div className="absolute top-3 left-3 flex items-center gap-2 px-2 py-1 bg-black/70 backdrop-blur border border-white/10">
                    {playing ? <Pause size={10} className="text-[#FFD60A]" fill="currentColor" /> : <Play size={10} className="text-[#FFD60A]" fill="currentColor" />}
                    <span className="font-mono text-[9px] uppercase tracking-widest text-white">{playing ? "Live" : "Pause"}</span>
                  </div>
                </div>
                <div className="px-4 py-3 flex items-center justify-between border-t border-zinc-800">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Présentation</div>
                    <div className="text-sm text-white">Horsepower Academy</div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleMute(e); }}
                    className="flex items-center gap-2 px-3 py-1.5 border border-[#FFD60A]/40 hover:bg-[#FFD60A] hover:text-black text-[#FFD60A] font-mono text-[10px] uppercase tracking-widest transition-colors"
                    data-testid="hero-video-sound-toggle"
                  >
                    {muted ? <><VolumeX size={11} /> Son</> : <><Volume2 size={11} /> ON</>}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

